import React, { useState } from 'react';
import { Divider } from '@material-ui/core';

import BookCateringCTAContainer from 'caterers/components/BookCateringCTAContainer';
import CatererHeader from 'caterers/components/CatererHeader';
import ClaimCatererProfileCTA from 'caterers/components/ClaimCatererProfileCTA';
import ClaimCatererProfileForm from 'caterers/components/ClaimCatererProfileForm';
import ClaimCatererProfileLargeCTA from 'caterers/components/ClaimCatererProfileLargeCTA';
import SimilarCaterersFactory from 'caterers/components/SimilarCaterersFactory';
import { searchBestRated } from 'lib/algolia/client';
import { stripCatererFieldsArray } from 'lib/algolia/utils';
import {
  fetchUnsignedCatererBySlug,
  fetchUnsignedCaterers,
} from 'lib/contentful/caterers';
import { INDEX_LOCALES, REDIRECT_LOCALE } from 'shared/constants';
import { handleStaticPaths } from 'shared/helpers/handleStaticPaths';
import MainLayout from 'shared/layouts/MainLayout';
import { fetchRatings } from 'shared/services/trustedShops/fetchRatings';
import Container from 'system/components/Container';

const DEFAULT_CITY = 'berlin';

function Caterer({ ratings, unsignedCaterer, similarCaterers, cityFromSlug }) {
  const [
    isClaimCatererProfileFormOpened,
    setIsClaimCatererProfileFormOpened,
  ] = useState(false);

  const openClaimCatererProfileForm = () =>
    setIsClaimCatererProfileFormOpened(true);
  const closeClaimCatererProfileForm = () =>
    setIsClaimCatererProfileFormOpened(false);

  return (
    <MainLayout ratings={ratings}>
      <Container>
        <CatererHeader caterer={unsignedCaterer.fields} />
        <ClaimCatererProfileCTA
          openClaimCatererProfileForm={openClaimCatererProfileForm}
          catererName={unsignedCaterer.fields.name}
        />
        <BookCateringCTAContainer
          citySlug={cityFromSlug}
          catererName={unsignedCaterer.fields.name}
          unsignedCatererAddress={`${unsignedCaterer.fields.address.street}, ${unsignedCaterer.fields.address.city}`}
          deliverySchedule={unsignedCaterer.fields.workingHours}
        />
        <ClaimCatererProfileLargeCTA
          catererName={unsignedCaterer.fields.name}
          openClaimCatererProfileForm={openClaimCatererProfileForm}
        />
        <Divider />
        <SimilarCaterersFactory
          city={cityFromSlug || DEFAULT_CITY}
          similarCaterers={similarCaterers}
        />
        <ClaimCatererProfileForm
          unsignedCaterer={unsignedCaterer.fields}
          isClaimCatererProfileFormOpened={isClaimCatererProfileFormOpened}
          closeClaimCatererProfileForm={closeClaimCatererProfileForm}
        />
      </Container>
    </MainLayout>
  );
}

export async function getStaticProps({ locale, params }) {
  if (locale === REDIRECT_LOCALE) {
    return {
      notFound: true,
    };
  }

  const catererCitySlugsFilter = `city_slugs:${params.city}`;

  const [ratings, unsignedCaterer, similarCaterers] = await Promise.all([
    fetchRatings(),
    fetchUnsignedCatererBySlug({
      unsignedCatererSlug: params.caterer,
    }),
    searchBestRated('', {
      facetFilters: [catererCitySlugsFilter],
      hitsPerPage: 3,
    }),
  ]);

  return {
    props: {
      cityFromSlug: params.city,
      similarCaterers: stripCatererFieldsArray(similarCaterers),
      unsignedCaterer,
      ratings,
      robots: 'noindex',
    },
  };
}

export const getStaticPaths = handleStaticPaths(async () => {
  const unsignedCaterers = await fetchUnsignedCaterers();

  let paths = [];
  INDEX_LOCALES.forEach((locale) => {
    paths.push(
      ...unsignedCaterers.map((unsignedCaterer) => ({
        params: {
          city: unsignedCaterer?.fields?.city?.fields?.slug || 'berlin',
          caterer: unsignedCaterer.fields.slug,
        },
        locale,
      }))
    );
  });

  return {
    paths,
    fallback: false,
  };
});

export default Caterer;
